// src/features/MeterAnalysis/MeterAnalysisPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Visibility, 
  Analytics,
  WaterDrop,
  LocalGasStation,
  Search,
  ShowChart,
  BarChart
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { useMeterData, METER_CONFIG } from './hooks/useMeterData';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart as RechartsBarChart, 
  Bar, 
  Legend 
} from 'recharts';

// --- Компонент графіка для лічильника ---
const MeterChart = ({ data, type = 'line', height = 200, unit = '' }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('meter_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
    value: item.value,
    consumption: item.consumption,
    fullDate: item.date
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === 'line' ? (
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'value') return [`${value} ${unit}`, t('meter_analysis_reading')];
              if (name === 'consumption') return [`${value} ${unit}`, t('meter_analysis_consumption')];
              return [value, name];
            }}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                return new Date(payload[0].payload.fullDate).toLocaleDateString('es-ES');
              }
              return label;
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: theme.palette.primary.dark }}
            name={t('meter_analysis_reading')}
          />
          <Line 
            type="monotone" 
            dataKey="consumption" 
            stroke={theme.palette.warning.main}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: theme.palette.warning.main, r: 3 }}
            name={t('meter_analysis_consumption')}
          />
        </LineChart>
      ) : (
        <RechartsBarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'value') return [`${value} ${unit}`, t('meter_analysis_reading')];
              if (name === 'consumption') return [`${value} ${unit}`, t('meter_analysis_consumption')];
              return [value, name];
            }}
          />
          <Legend />
          <Bar 
            dataKey="value" 
            fill={theme.palette.primary.main}
            name={t('meter_analysis_reading')}
            radius={[4, 4, 0, 0]}
          />
          <Bar 
            dataKey="consumption" 
            fill={theme.palette.warning.main}
            name={t('meter_analysis_consumption')}
            radius={[4, 4, 0, 0]}
          />
        </RechartsBarChart>
      )}
    </ResponsiveContainer>
  );
};

// Картка лічильника з графіком
const MeterCard = ({ meterKey, data, statistics, config, onViewDetails }) => {
  const { t } = useI18n();
  const theme = useTheme();
  const [chartType, setChartType] = useState('line');

  // Останні 10 записів для графіка
  const chartData = data.slice(-10);

  // Отримуємо локалізовану назву лічильника
  const getLocalizedMeterName = (meterKey) => {
    const meterTranslations = {
      'Cont. agua túnel': t('meter_water_tunnel'),
      'Cont. agua pozo': t('meter_water_well'),
      'Cont. agua lavadoras': t('meter_water_washers'),
      'Cont. agua calderas': t('meter_water_boilers'),
      'Cont. propano depósito': t('meter_propane_tank'),
      'Cont. propano caldera': t('meter_propane_boiler'),
      'Cont. propano cal. saba': t('meter_propane_saba'),
      'Cont. propano cal. medio': t('meter_propane_medium'),
      'Cont. propano cal. hibri': t('meter_propane_hybrid'),
      'Cont. propano secadora': t('meter_propane_dryer')
    };
    return meterTranslations[meterKey] || meterKey;
  };

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 40px 0 rgba(0,0,0,0.12)',
            border: `1px solid ${alpha(config.type === 'water' ? theme.palette.info.main : theme.palette.warning.main, 0.3)}`,
          }
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Заголовок лічильника */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${config.type === 'water' ? theme.palette.info.main : theme.palette.warning.main} 0%, ${config.type === 'water' ? theme.palette.info.dark : theme.palette.warning.dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(config.type === 'water' ? theme.palette.info.main : theme.palette.warning.main, 0.3)}`
                }}
              >
                {config.type === 'water' ? 
                  <WaterDrop sx={{ color: 'white', fontSize: 24 }} /> :
                  <LocalGasStation sx={{ color: 'white', fontSize: 24 }} />
                }
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    lineHeight: 1.2,
                    mb: 0.5
                  }}
                >
                  {getLocalizedMeterName(meterKey)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip 
                    label={config.unit} 
                    size="small"
                    color={config.type === 'water' ? 'info' : 'warning'}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    label={config.type === 'water' ? t('meter_type_water') : t('meter_type_propane')}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Графік */}
          {chartData.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('meter_analysis_trend')}
                </Typography>
                <Select
                  size="small"
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="line">{t('chart_type_line')}</MenuItem>
                  <MenuItem value="bar">{t('chart_type_bar')}</MenuItem>
                </Select>
              </Box>
              <MeterChart data={chartData} type={chartType} height={120} unit={config.unit} />
            </Box>
          )}

          {/* Статистика */}
          {data.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('meter_analysis_total_consumption')}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: config.type === 'water' ? 'info.main' : 'warning.main' }}>
                    {statistics.totalConsumption.toLocaleString('es-ES')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('meter_analysis_avg_consumption')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {statistics.averageConsumption.toFixed(1)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Останні показники */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              {t('meter_analysis_latest_readings')}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>
                      {t('meter_analysis_date')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>
                      {config.unit}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.slice(-3).reverse().map((record, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ py: 1, fontSize: '0.8rem' }}>
                        {new Date(record.date).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: '2-digit'
                        })}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Chip 
                          label={record.value.toLocaleString('es-ES')} 
                          size="small"
                          color={config.type === 'water' ? 'info' : 'warning'}
                          variant="filled"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Кнопка деталей */}
          <Box sx={{ mt: 'auto' }}>
            <Button 
              fullWidth
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => onViewDetails(meterKey)}
              sx={{
                borderRadius: '8px',
                py: 1,
                fontWeight: 600,
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {t('meter_analysis_view_details')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default function MeterAnalysisPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [meterType, setMeterType] = useState('all');

  const { allMetersData, loading, error } = useMeterData();

  // Фільтруємо лічильники за пошуком та типом
  const filteredMeters = useMemo(() => {
    if (!METER_CONFIG) return [];
    
    const meters = Object.keys(METER_CONFIG);
    
    let filtered = meters;
    
    // Фільтр по типу
    if (meterType !== 'all') {
      filtered = filtered.filter(meterKey => 
        METER_CONFIG[meterKey].type === meterType
      );
    }
    
    // Фільтр по пошуку
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(meterKey => {
        const localizedName = t(`meter_${meterKey.replace(/[.\s]/g, '_').toLowerCase()}`) || meterKey;
        return localizedName.toLowerCase().includes(lowerSearchTerm);
      });
    }
    
    return filtered;
  }, [searchTerm, meterType, t]);

  const handleViewDetails = (meterKey) => {
    navigate(`/meter-analysis/${meterKey}`);
  };

  // Лічильники з даними
  const metersWithData = useMemo(() => {
    return allMetersData ? Object.keys(allMetersData).filter(key => allMetersData[key]?.data?.length > 0) : [];
  }, [allMetersData]);

  // Статистика по всіх лічильниках
  const totalStats = useMemo(() => {
    if (!METER_CONFIG) return { totalMeters: 0, waterMeters: 0, propaneMeters: 0, metersWithData: 0 };
    
    const waterMeters = Object.keys(METER_CONFIG).filter(
      key => METER_CONFIG[key].type === 'water'
    ).length;
    
    const propaneMeters = Object.keys(METER_CONFIG).filter(
      key => METER_CONFIG[key].type === 'propane'
    ).length;

    return {
      totalMeters: Object.keys(METER_CONFIG).length,
      waterMeters,
      propaneMeters,
      metersWithData: metersWithData.length
    };
  }, [metersWithData]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t('common_loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок сторінки */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <Analytics sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              {t('meter_analysis_title')}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {t('meter_analysis_subtitle')}
            </Typography>
          </Box>
        </Stack>

        {/* Статистика */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.primary.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {totalStats.totalMeters}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('meter_stats_total_counters')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.info.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main' }}>
                  {totalStats.waterMeters}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('meter_stats_water_counters')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.warning.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.main' }}>
                  {totalStats.propaneMeters}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('meter_stats_propane_counters')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.success.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main' }}>
                  {totalStats.metersWithData}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('meter_analysis_with_data')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Пошук та фільтри */}
        <Card sx={{ mb: 4, p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={t('meter_analysis_search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                }}
                placeholder={t('meter_analysis_search_placeholder')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('meter_analysis_filter_type')}</InputLabel>
                <Select
                  value={meterType}
                  onChange={(e) => setMeterType(e.target.value)}
                  label={t('meter_analysis_filter_type')}
                >
                  <MenuItem value="all">{t('meter_type_all')}</MenuItem>
                  <MenuItem value="water">{t('meter_type_water')}</MenuItem>
                  <MenuItem value="propane">{t('meter_type_propane')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                {t('meter_analysis_found')}: <strong>{filteredMeters.length}</strong> {t('meter_analysis_counters')}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Вкладки */}
      <Card sx={{ mb: 4 }}>
        <Tabs 
          value={tabIndex} 
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="fullWidth"
        >
          <Tab label={t('meter_analysis_overview')} />
          <Tab 
            label={t('meter_analysis_comparison')} 
            disabled={metersWithData.length < 2}
          />
        </Tabs>
      </Card>

      {/* Контент вкладок */}
      {tabIndex === 0 ? (
        // Вкладка огляду
        <Grid container spacing={3}>
          {filteredMeters.map(meterKey => (
            <MeterCard
              key={meterKey}
              meterKey={meterKey}
              data={allMetersData?.[meterKey]?.data || []}
              statistics={allMetersData?.[meterKey]?.statistics || {
                totalConsumption: 0, 
                averageConsumption: 0, 
                maxConsumption: 0, 
                minConsumption: 0, 
                daysWithData: 0, 
                recordsCount: 0
              }}
              config={METER_CONFIG[meterKey]}
              onViewDetails={handleViewDetails}
            />
          ))}
        </Grid>
      ) : (
        // Вкладка порівняння
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('meter_analysis_comparison_coming_soon')}
          </Typography>
        </Card>
      )}

      {/* Інформаційний блок */}
      {totalStats.metersWithData > 0 && (
        <Card 
          sx={{ 
            mt: 4, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              {t('meter_analysis_click_for_details')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}


